import { TrainingDay, TrainingDayExercise, Exercise, MuscleGroup } from '../database';

export interface MuscleGroupStats {
  muscle: MuscleGroup;
  primarySets: number;
  secondarySets: number;
  totalSets: number;
  exercises: string[]; // List of exercise names targeting this muscle
}

export interface RoutineMuscleAnalysis {
  totalSets: number;
  muscleStats: MuscleGroupStats[];
  topPrimaryMuscles: MuscleGroupStats[];
  balance: {
    pushPullRatio: number;
    upperLowerRatio: number;
    verticalHorizontalRatio: number;
  };
}

const MUSCLE_GROUPS: MuscleGroup[] = [
  'Chest', 'Back', 'Lats', 'Rhomboids', 'Traps', 'Shoulders',
  'Front Delts', 'Side Delts', 'Rear Delts', 'Biceps', 'Triceps', 'Forearms',
  'Core', 'Abs', 'Obliques', 'Quads', 'Hamstrings', 'Glutes', 'Calves',
  'Cardiovascular', 'Full Body'
];

const PUSH_MUSCLES: MuscleGroup[] = ['Chest', 'Shoulders', 'Front Delts', 'Side Delts', 'Triceps'];
const PULL_MUSCLES: MuscleGroup[] = ['Back', 'Lats', 'Rhomboids', 'Traps', 'Rear Delts', 'Biceps', 'Forearms'];
const UPPER_MUSCLES: MuscleGroup[] = ['Chest', 'Back', 'Lats', 'Rhomboids', 'Traps', 'Shoulders', 'Front Delts', 'Side Delts', 'Rear Delts', 'Biceps', 'Triceps', 'Forearms'];
const LOWER_MUSCLES: MuscleGroup[] = ['Quads', 'Hamstrings', 'Glutes', 'Calves'];
const VERTICAL_PULL: MuscleGroup[] = ['Lats', 'Rhomboids'];
const HORIZONTAL_PULL: MuscleGroup[] = ['Back', 'Rear Delts'];

export function analyzeRoutineMuscleGroups(
  trainingDays: TrainingDay[],
  trainingDayExercises: TrainingDayExercise[],
  exercises: Exercise[]
): RoutineMuscleAnalysis {
  // Initialize muscle stats
  const muscleStatsMap = new Map<string, MuscleGroupStats>();
  MUSCLE_GROUPS.forEach(muscle => {
    muscleStatsMap.set(muscle, {
      muscle,
      primarySets: 0,
      secondarySets: 0,
      totalSets: 0,
      exercises: []
    });
  });

  let totalSets = 0;

  // Iterate through all training days
  trainingDays.forEach(trainingDay => {
    // Get exercises for this training day
    const dayExercises = trainingDayExercises.filter(tde => tde.trainingDayId === trainingDay.id);
    
    dayExercises.forEach(trainingDayExercise => {
      // Find the exercise details
      const exercise = exercises.find(ex => ex.id === trainingDayExercise.exerciseId);
      if (!exercise) return;

      const numSets = trainingDayExercise.sets.length;
      totalSets += numSets;

      // Add sets for primary muscles
      exercise.primaryMuscles?.forEach(muscle => {
        const stats = muscleStatsMap.get(muscle);
        if (stats) {
          stats.primarySets += numSets;
          stats.totalSets += numSets;
          if (!stats.exercises.includes(exercise.name)) {
            stats.exercises.push(exercise.name);
          }
        }
      });

      // Add sets for secondary muscles (typically count as half volume)
      exercise.secondaryMuscles?.forEach(muscle => {
        const stats = muscleStatsMap.get(muscle);
        if (stats) {
          stats.secondarySets += numSets;
          stats.totalSets += numSets;
          if (!stats.exercises.includes(exercise.name)) {
            stats.exercises.push(exercise.name);
          }
        }
      });
    });
  });

  // Convert to array and filter out muscles with no sets
  const muscleStats = Array.from(muscleStatsMap.values())
    .filter(stats => stats.totalSets > 0)
    .sort((a, b) => {
      // Sort by primary sets first, then by total sets as tiebreaker
      if (b.primarySets !== a.primarySets) {
        return b.primarySets - a.primarySets;
      }
      return b.totalSets - a.totalSets;
    });

  // Find top primary muscles (muscles with the most primary volume)
  const topPrimaryMuscles = Array.from(muscleStatsMap.values())
    .filter(stats => stats.primarySets > 0)
    .sort((a, b) => b.primarySets - a.primarySets)
    .slice(0, 5);

  // Calculate balance ratios
  const pushSets = muscleStats
    .filter(stats => PUSH_MUSCLES.includes(stats.muscle))
    .reduce((sum, stats) => sum + stats.primarySets, 0);

  const pullSets = muscleStats
    .filter(stats => PULL_MUSCLES.includes(stats.muscle))
    .reduce((sum, stats) => sum + stats.primarySets, 0);

  const upperSets = muscleStats
    .filter(stats => UPPER_MUSCLES.includes(stats.muscle))
    .reduce((sum, stats) => sum + stats.primarySets, 0);

  const lowerSets = muscleStats
    .filter(stats => LOWER_MUSCLES.includes(stats.muscle))
    .reduce((sum, stats) => sum + stats.primarySets, 0);

  const verticalPullSets = muscleStats
    .filter(stats => VERTICAL_PULL.includes(stats.muscle))
    .reduce((sum, stats) => sum + stats.primarySets, 0);

  const horizontalPullSets = muscleStats
    .filter(stats => HORIZONTAL_PULL.includes(stats.muscle))
    .reduce((sum, stats) => sum + stats.primarySets, 0);

  return {
    totalSets,
    muscleStats,
    topPrimaryMuscles,
    balance: {
      pushPullRatio: pullSets > 0 ? pushSets / pullSets : 0,
      upperLowerRatio: lowerSets > 0 ? upperSets / lowerSets : 0,
      verticalHorizontalRatio: horizontalPullSets > 0 ? verticalPullSets / horizontalPullSets : 0
    }
  };
}

export function getMuscleGroupColor(muscle: MuscleGroup): string {
  // Color coding for different muscle groups
  const colorMap: Record<MuscleGroup, string> = {
    'Chest': '#3B82F6', // blue
    'Back': '#10B981', // emerald  
    'Lats': '#059669', // emerald-600
    'Rhomboids': '#047857', // emerald-700
    'Traps': '#065F46', // emerald-800
    'Shoulders': '#F59E0B', // amber
    'Front Delts': '#F59E0B', // amber
    'Side Delts': '#D97706', // amber-600
    'Rear Delts': '#B45309', // amber-700
    'Biceps': '#8B5CF6', // violet
    'Triceps': '#A855F7', // purple
    'Forearms': '#7C3AED', // violet-600
    'Core': '#EF4444', // red
    'Abs': '#EF4444', // red
    'Obliques': '#DC2626', // red-600
    'Quads': '#EC4899', // pink
    'Hamstrings': '#DB2777', // pink-600
    'Glutes': '#BE185D', // pink-700
    'Calves': '#9D174D', // pink-800
    'Cardiovascular': '#6B7280', // gray
    'Full Body': '#374151' // gray-700
  };
  
  return colorMap[muscle] || '#6B7280'; // default gray
}

export function formatRatio(ratio: number): string {
  if (ratio === 0) return '0:1';
  if (ratio < 1) return `1:${(1/ratio).toFixed(1)}`;
  return `${ratio.toFixed(1)}:1`;
}